import type { ReactNode } from 'react'

interface PageTitleProps {
  /** The small uppercase label above the title. Omitted when the page has none. */
  eyebrow?: ReactNode
  children: ReactNode
  /** `page` for app screens, `compact` for the sign-in card. */
  size?: 'page' | 'compact'
  className?: string
}

/**
 * Every page title in the app (DEC-183, UI_SDD §R.1.1): a purple eyebrow over a
 * title filled with the brand gradient.
 *
 * The gradient is the Athenaworks headline gradient on light and a lightened
 * version on dark, both defined in index.css (`.td-gradient-headline`), so every
 * stop clears 4.5:1 in either theme. It is for headlines only, never body text.
 * `w-fit` keeps the gradient spanning the words rather than the whole row.
 */
export function PageTitle({ eyebrow, children, size = 'page', className = '' }: PageTitleProps) {
  const titleSize =
    size === 'compact' ? 'text-[26px] leading-[1.15]' : 'text-[28px] leading-[1.1]'
  return (
    <div className={className}>
      {eyebrow && (
        <div className="font-semibold text-[12px] leading-none tracking-[0.18em] uppercase text-accent-text mb-2">
          {eyebrow}
        </div>
      )}
      <h1 className={`td-gradient-headline w-fit font-semibold tracking-[-0.02em] m-0 ${titleSize}`}>
        {children}
      </h1>
    </div>
  )
}
